import React, { useEffect, useState } from "react";
import { AlertTriangle, Plane, Power, PowerOff, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const money = (value, currency = "EUR") => Number(value || 0).toLocaleString("fr-FR", {
  style: "currency", currency, maximumFractionDigits: 0,
});

const Status = ({ value }) => (
  <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
    value === "ready" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : value === "failed" ? "border-rose-200 bg-rose-50 text-rose-700"
        : "border-slate-200 bg-slate-100 text-slate-600"
  }`}>{value}</span>
);

export default function AdminBspData() {
  const [datasets, setDatasets] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/bsp-data/admin/datasets");
      setDatasets(data.datasets || []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (dataset) => {
    const action = dataset.status === "ready" ? "disable" : "enable";
    try {
      await api.post(`/bsp-data/admin/datasets/${encodeURIComponent(dataset.dataset_id)}/${action}`);
      toast.success(action === "disable" ? "Dataset BSP désactivé" : "Dataset BSP réactivé");
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-bsp-data">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#17201A]">Lodge BSP Data</h1>
          <p className="text-sm text-slate-500">Distribution des ventes BSP par agence et destination.</p>
        </div>
        <button type="button" onClick={() => setShowImport(true)} data-testid="open-bsp-import"
          className="inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-4 py-2.5 text-sm font-semibold text-white">
          <Upload size={16} /> Importer un export BSP
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-[760px] w-full text-sm">
          <thead className="bg-[#FAFAF6] text-left text-[11px] uppercase tracking-wide text-slate-400">
            <tr>{["Marché", "Période", "Statut", "Agences", "Ventes industrie", "Documents", "Fichier", "Action"].map((h) => (
              <th key={h} className="px-3 py-3 font-semibold">{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {datasets.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Aucun dataset BSP.</td></tr>}
            {datasets.map((dataset) => (
              <tr key={dataset.dataset_id} data-testid={`bsp-row-${dataset.destination_code}`}>
                <td className="px-3 py-3 font-medium text-[#17201A]">{dataset.source_market} → {dataset.destination_code}</td>
                <td className="px-3 py-3 text-slate-600">{dataset.period_start} → {dataset.period_end}</td>
                <td className="px-3 py-3"><Status value={dataset.status} /></td>
                <td className="px-3 py-3 tabular-nums">{dataset.agency_count ?? dataset.row_count ?? "—"}</td>
                <td className="px-3 py-3 tabular-nums">{money(dataset.total_industry_sales, dataset.currency)}</td>
                <td className="px-3 py-3 tabular-nums">{Number(dataset.total_industry_documents || 0).toLocaleString("fr-FR")}</td>
                <td className="max-w-[180px] truncate px-3 py-3 text-xs text-slate-400">{dataset.source_filename || "—"}</td>
                <td className="px-3 py-3">
                  {(dataset.status === "ready" || dataset.status === "disabled") && (
                    <button type="button" onClick={() => toggle(dataset)} className="rounded-md border border-slate-200 p-1.5 text-slate-500"
                      title={dataset.status === "ready" ? "Désactiver" : "Réactiver"}>
                      {dataset.status === "ready" ? <PowerOff size={15} /> : <Power size={15} />}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showImport && <BspImport onClose={() => setShowImport(false)} onPublished={() => { setShowImport(false); load(); }} />}
    </div>
  );
}

function BspImport({ onClose, onPublished }) {
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(false);

  const analyze = async () => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/bsp-data/admin/imports/analyze", form);
      setAnalysis(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post("/bsp-data/admin/imports/publish", {
        upload_id: analysis.upload_id,
        destination_code: "MU",
        period_start: analysis.period_start,
        period_end: analysis.period_end,
        source_market: analysis.source_market || "FR",
        currency: analysis.currency || "EUR",
        replace,
      });
      toast.success("Dataset BSP Maurice publié");
      onPublished();
    } catch (error) {
      if (error.response?.status === 409) setReplace(true);
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="font-heading font-bold text-[#17201A]">Importer les ventes BSP agences</h2>
          <button type="button" onClick={onClose} className="p-1 text-slate-400"><X size={18} /></button>
        </div>
        <div className="space-y-4 p-6">
          {!analysis ? <>
            <p className="text-sm text-slate-500">Le fichier est analysé sans publication. Seules les données agence/destination nécessaires seront conservées.</p>
            <input type="file" accept=".csv" data-testid="bsp-file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full rounded-lg border border-dashed border-slate-300 px-4 py-6 text-sm" />
            <div className="flex justify-end"><button type="button" disabled={!file || busy} onClick={analyze}
              className="rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
              {busy ? "Analyse…" : "Analyser"}
            </button></div>
          </> : <>
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-[#FAFAF6] p-4 text-sm sm:grid-cols-4">
              <div><p className="text-xs text-slate-400">Marché</p><p className="font-semibold">{analysis.source_market} → MU</p></div>
              <div><p className="text-xs text-slate-400">Période</p><p className="font-semibold">{analysis.period_start} → {analysis.period_end}</p></div>
              <div><p className="text-xs text-slate-400">Agences Maurice</p><p className="font-semibold">{analysis.mauritius?.agency_count ?? "—"}</p></div>
              <div><p className="text-xs text-slate-400">Ventes industrie</p><p className="font-semibold">{money(analysis.mauritius?.total_industry_sales, analysis.currency)}</p></div>
            </div>
            {analysis.airline_context && <p className="text-xs text-slate-500">Contexte du rapport : {analysis.airline_context}. La vue membre utilise uniquement les métriques Industrie.</p>}
            {analysis.warnings?.map((warning) => <p key={warning} className="flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"><AlertTriangle size={15} />{warning}</p>)}
            {replace && <label className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> Remplacer le dataset BSP existant pour cette période.</label>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAnalysis(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm">Retour</button>
              <button type="button" disabled={!analysis.can_publish || busy || !analysis.period_start || !analysis.period_end} onClick={publish}
                className="inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
                <Plane size={15} /> {busy ? "Publication…" : replace ? "Remplacer" : "Publier Maurice"}
              </button>
            </div>
          </>}
        </div>
      </div>
    </div>
  );
}
